import { Injectable } from '@angular/core';
import {
  AuthSession,
  confirmSignUp,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  signUp,
  signIn,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { ApplicationConfig } from '../../core/config/application.config';
import { AuthToken } from '../../domain/auth/model/auth-token';
import {
  AuthProvider,
  LoginResult,
} from '../../domain/auth/model/auth-provider';
import { SignUpData } from '../../domain/auth/model/sign-up-data';
import {
  CognitoAuthErrorUtil,
  CognitoAuthOperation,
  isAlreadySignedInError,
} from '../util/cognito-auth-error.util';

@Injectable({ providedIn: 'root' })
export class CognitoAuthService implements AuthProvider {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: ApplicationConfig.cognito!.userPoolId,
          userPoolClientId: ApplicationConfig.cognito!.userPoolWebClientId,
          loginWith: {
            email: true,
          },
        },
      },
    });
  }

  public async login(username: string, password: string): Promise<LoginResult> {
    let result: Awaited<ReturnType<typeof signIn>>;
    try {
      result = await this.signInFresh(username, password);
    } catch (ex: any) {
      throw this.mappedError(ex, 'LOGIN');
    }

    switch (result.nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return { nextStep: 'SET_PASSWORD' };
      // Amplify answers an unconfirmed account with this step instead of
      // throwing UserNotConfirmedException; it comes only after the password matched.
      case 'CONFIRM_SIGN_UP':
        throw this.mappedError({ name: 'UserNotConfirmedException' }, 'LOGIN');
      default:
        return { nextStep: 'DONE' };
    }
  }

  /**
   * Signs in as the person who typed the credentials. A session Amplify still
   * holds (another tab, a sign-out that did not finish) is dropped first, never
   * reused: reusing it signed people in as the previous account.
   */
  private async signInFresh(username: string, password: string) {
    try {
      return await signIn({ username, password });
    } catch (ex) {
      if (!isAlreadySignedInError(ex)) {
        throw ex;
      }
      await signOut();
      return signIn({ username, password });
    }
  }

  public async setPassword(password: string, username?: string): Promise<any> {
    try {
      return await confirmSignIn({
        challengeResponse: password,
        options: {
          userAttributes: {
            given_name: '',
            family_name: '',
          },
        },
      });
    } catch (ex: any) {
      const message = String(ex?.message ?? ex ?? '');
      const shouldFallback =
        message.includes('given_name') ||
        message.includes('family_name') ||
        message.includes('Invalid attributes');

      if (!shouldFallback || !username) {
        const mapped = CognitoAuthErrorUtil.getErrorMessage(ex, 'SET_PASSWORD');
        const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
        (error as any).cause = ex;
        throw error;
      }

      try {
        return await confirmSignIn({
          challengeResponse: password,
          options: {
            userAttributes: {
              given_name: username,
              family_name: username,
            },
          },
        });
      } catch (fallbackEx: any) {
        const mapped = CognitoAuthErrorUtil.getErrorMessage(
          fallbackEx,
          'SET_PASSWORD'
        );
        const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
        (error as any).cause = fallbackEx;
        throw error;
      }
    }
  }

  public async changePassword(
    currentPassword: string,
    password: string
  ): Promise<any> {
    try {
      return await updatePassword({
        oldPassword: currentPassword,
        newPassword: password,
      });
    } catch (ex: any) {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(
        ex,
        'CHANGE_PASSWORD'
      );
      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      throw error;
    }
  }

  public async confirmResetPassword(
    username: string,
    confirmationCode: string,
    password: string
  ): Promise<any> {
    try {
      return await confirmResetPassword({
        username,
        confirmationCode,
        newPassword: password,
      });
    } catch (ex: any) {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(
        ex,
        'RESET_PASSWORD_CONFIRM'
      );
      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      throw error;
    }
  }

  public resetPassword(username: string): Promise<any> {
    return resetPassword({ username }).catch(ex => {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(
        ex,
        'RESET_PASSWORD_START'
      );

      if (mapped.silent) {
        return Promise.resolve();
      }

      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      return Promise.reject(error);
    });
  }

  /**
   * Registers a Submitter directly with Cognito. The role is never sent: the
   * app client cannot write it, and Cognito sets it after confirmation.
   */
  public async signUp(data: SignUpData): Promise<void> {
    const userAttributes: Record<string, string> = {
      email: data.email,
      given_name: data.firstName,
      family_name: data.lastName,
      locale: data.locale,
    };
    if (data.company) {
      userAttributes['custom:company'] = data.company;
    }

    try {
      await signUp({
        username: data.email,
        password: data.password,
        options: { userAttributes },
      });
    } catch (ex: any) {
      throw this.mappedError(ex, 'SIGN_UP');
    }
  }

  public async confirmSignUp(username: string, code: string): Promise<void> {
    try {
      await confirmSignUp({ username, confirmationCode: code });
    } catch (ex: any) {
      throw this.mappedError(ex, 'CONFIRM_SIGN_UP');
    }
  }

  public async resendSignUpCode(username: string): Promise<void> {
    try {
      await resendSignUpCode({ username });
    } catch (ex: any) {
      // An unknown e-mail looks the same as a sent code.
      if (
        CognitoAuthErrorUtil.getErrorMessage(ex, 'RESEND_SIGN_UP_CODE').silent
      ) {
        return;
      }
      throw this.mappedError(ex, 'RESEND_SIGN_UP_CODE');
    }
  }

  private mappedError(ex: unknown, operation: CognitoAuthOperation): Error {
    const mapped = CognitoAuthErrorUtil.getErrorMessage(ex, operation);
    const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
    (error as any).cause = ex;
    return error;
  }

  public async logout() {
    try {
      await signOut({ global: true });
    } catch {
      await signOut();
    }
  }

  public async refresh(): Promise<AuthToken> {
    return fetchAuthSession({ forceRefresh: true }).then(session =>
      this.generateToken(session)
    );
  }

  public async generateToken(session?: AuthSession): Promise<AuthToken> {
    const { tokens } = session || (await fetchAuthSession());
    const { idToken } = tokens!;
    if (!idToken) {
      throw new Error('Missing idToken');
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    const exp = idToken.payload['exp'];
    const expiresIn =
      typeof exp === 'number' ? Math.max(exp - nowSeconds, 0) : 0;

    return {
      accessToken: idToken.toString(),
      refreshToken: '',
      expiresIn,
      email: idToken.payload['email'] ?? '',
      userId: 0,
      tokenType: 'Bearer',
      scope: '',
      changePasswordRequired: false,
    } as AuthToken;
  }
}
